import { Check, Loader2, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from 'components/ui/badge';
import { Input } from 'components/ui/input';
import { useTheme } from 'components/core/theme-provider';
import type { IComposioCatalogToolkit } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { cn } from 'lib/utils';
import { resolveComposioToolLogo } from './composio-logos';

export type ConnectorCatalogRow =
  | {
      kind: 'open_connector';
      id: string;
      name: string;
      service: string;
      logo: string | null;
      isSelected: boolean;
    }
  | {
      kind: 'composio';
      entry: IComposioCatalogToolkit;
      isSelected: boolean;
    };

type ConnectorAppCatalogProps = {
  rows: ConnectorCatalogRow[];
  showList: boolean;
  isLoading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  onSelectToolkit: (slug: string) => void;
  onSelectOpenConnector: (service: string) => void;
  disabled: boolean;
  suggestions: readonly string[];
  isSuggestionSelected: (suggestion: string) => boolean;
};

function CatalogRow({
  name,
  subtitle,
  logo,
  badge,
  isSelected,
  onSelect,
}: {
  name: string;
  subtitle: string;
  logo: string | null;
  badge?: string;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={isSelected}
      className={cn(
        'flex w-full items-center gap-3 rounded-md border px-2.5 py-2 text-left transition-colors',
        isSelected
          ? 'border-primary/40 bg-primary/5 hover:bg-primary/10'
          : 'border-transparent hover:border-border hover:bg-muted/50'
      )}
      onClick={onSelect}
    >
      {logo ? (
        <img
          src={logo}
          alt=""
          className="h-8 w-8 shrink-0 rounded-md border bg-background object-contain p-0.5"
          loading="lazy"
        />
      ) : (
        <span
          aria-hidden
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-muted text-3xs font-semibold uppercase text-muted-foreground"
        >
          {name.slice(0, 2)}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium leading-5">{name}</span>
        <span className="block truncate text-xs text-muted-foreground">{subtitle}</span>
      </span>
      {badge ? (
        <Badge variant="outline" className="shrink-0 text-3xs">
          {badge}
        </Badge>
      ) : null}
      {isSelected ? (
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <Check className="h-3 w-3" aria-hidden />
        </span>
      ) : (
        <span className="h-5 w-5 shrink-0 rounded-full border border-border" aria-hidden />
      )}
    </button>
  );
}

export function ConnectorAppCatalog({
  rows,
  showList,
  isLoading,
  search,
  onSearchChange,
  onSelectToolkit,
  onSelectOpenConnector,
  disabled,
  suggestions,
  isSuggestionSelected,
}: ConnectorAppCatalogProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const suggestionText = theme === 'dark' ? 'text-true-white' : 'text-grey-900';
  const customAuthLabel = t('CUSTOM_AUTH_REQUIRED', { defaultValue: 'Custom auth' });

  if (showList) {
    return (
      <div className="space-y-2 rounded-lg border bg-muted/20 p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={t('SEARCH_CONNECTOR_APPS', {
              defaultValue: 'Search apps (Gmail, GitHub, Slack…)',
            })}
            disabled={disabled}
            className="h-9 border-border/60 bg-background pl-8 text-sm"
          />
        </div>
        <div className="space-y-1">
          {rows.map((row) =>
            row.kind === 'open_connector' ? (
              <CatalogRow
                key={row.id}
                name={row.name}
                subtitle={row.service.toUpperCase()}
                logo={row.logo}
                isSelected={row.isSelected}
                onSelect={() => onSelectOpenConnector(row.service)}
              />
            ) : (
              <CatalogRow
                key={row.entry.slug}
                name={row.entry.name}
                subtitle={row.entry.slug}
                logo={resolveComposioToolLogo(row.entry.slug) || row.entry.logo || null}
                badge={
                  !row.entry.composioManagedAuth && !row.entry.noAuth ? customAuthLabel : undefined
                }
                isSelected={row.isSelected}
                onSelect={() => onSelectToolkit(row.entry.slug)}
              />
            )
          )}
          {rows.length === 0 ? (
            <p className="px-2 py-4 text-center text-sm text-muted-foreground">
              {t('NO_APPS_FOUND', { defaultValue: 'No apps found.' })}
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-dashed px-3 py-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t('LOADING_APPS', { defaultValue: 'Loading apps…' })}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {suggestions.map((suggestion) => (
        <Badge
          key={suggestion}
          variant={isSuggestionSelected(suggestion) ? 'default' : 'outline'}
          className={cn('cursor-pointer text-xs', suggestionText)}
          onClick={() => onSelectToolkit(suggestion)}
        >
          {suggestion}
        </Badge>
      ))}
    </div>
  );
}
